import { ExternalLink, Globe } from 'lucide-react'

// External reputation monitoring tools.
const EXTERNAL_TOOLS = [
  {
    name: 'Google Postmaster Tools',
    url: 'https://postmaster.google.com/',
    description: 'Domain and IP reputation, spam rate, delivery errors for Gmail',
  },
  {
    name: 'Microsoft SNDS',
    url: 'https://sendersupport.olc.protection.outlook.com/snds/',
    description: 'IP reputation, complaint rate, spam traps for Outlook/Hotmail',
  },
  {
    name: 'Yahoo Postmaster',
    url: 'https://postmaster.yahooinc.com/',
    description: 'Delivery performance and reputation for Yahoo Mail',
  },
] as const

// ISP open rate breakdown — shows how our emails perform across major ISPs.
// TODO: Wire to real analytics data once open tracking events are aggregated.
const ISP_PLACEHOLDER_DATA = [
  { isp: 'Gmail', domain: 'gmail.com', openRate: '--' },
  { isp: 'Outlook', domain: 'outlook.com', openRate: '--' },
  { isp: 'Yahoo', domain: 'yahoo.com', openRate: '--' },
  { isp: 'iCloud', domain: 'icloud.com', openRate: '--' },
  { isp: 'Other', domain: 'other', openRate: '--' },
] as const

export function IspReputationPanel(): React.JSX.Element {
  return (
    <div className="rounded-xl border border-surface-3 bg-surface-0">
      <div className="flex items-center gap-2 border-b border-surface-3 px-5 py-4">
        <Globe className="h-5 w-5 text-text-secondary" />
        <h2 className="text-lg font-medium text-text-primary">ISP Reputation</h2>
      </div>

      {/* Open rate by ISP */}
      <div className="border-b border-surface-2 px-5 py-4">
        <h3 className="mb-3 text-sm font-medium text-text-secondary">Open Rate by ISP</h3>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          {ISP_PLACEHOLDER_DATA.map((isp) => (
            <div key={isp.domain} className="rounded-lg bg-surface-1 p-3 text-center">
              <p className="text-xs font-medium text-text-secondary">{isp.isp}</p>
              <p className="mt-1 text-xl font-semibold text-text-primary">{isp.openRate}</p>
            </div>
          ))}
        </div>
        <p className="mt-2 text-xs text-text-tertiary">
          Open rates will populate once tracking data is collected.
        </p>
      </div>

      {/* External tools */}
      <div className="px-5 py-4">
        <h3 className="mb-3 text-sm font-medium text-text-secondary">External Monitoring Tools</h3>
        <div className="grid gap-3 sm:grid-cols-3">
          {EXTERNAL_TOOLS.map((tool) => (
            <a
              key={tool.name}
              href={tool.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-start gap-3 rounded-lg border border-surface-2 p-3 transition-colors hover:border-admin-300 hover:bg-admin-50/50"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-text-primary group-hover:text-admin-700">
                  {tool.name}
                </p>
                <p className="mt-0.5 text-xs text-text-tertiary">{tool.description}</p>
              </div>
              <ExternalLink className="mt-0.5 h-4 w-4 shrink-0 text-text-tertiary group-hover:text-admin-500" />
            </a>
          ))}
        </div>
      </div>
    </div>
  )
}
